import {
    Button,
    Card,
    Col,
    Divider,
    Input,
    Tooltip,
    Row,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Alert,
    List,
    Typography, Modal, Grid
} from "antd";
import {
    InfoCircleOutlined,
    LoadingOutlined,
    SearchOutlined
} from '@ant-design/icons';
import {
    breakpointsHandler,
    defineMerge,
    getCompressionAdditionalInfo, getModel,
    isIndividual,
    textGenerator
} from "./Util";
import React, {startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {AngleSvg, RotatableCircle} from "./Canvas";
import {ConfigContext} from "./App";
import {getCompressionColor} from "./ColorUtil";
import {DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS} from "./GeometryUtil";


const antIcon = <div><LoadingOutlined style={{fontSize: 60}} spin/><span>Loading...</span></div>;


const generateFile = (data, dynamicColumns, fileName) => {
    const fileData = textGenerator(data, dynamicColumns)
    const blob = new Blob([fileData], {type: "text/plain"})
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.download = `${fileName}.txt`
    link.href = url
    link.click()
}

const downloadPymol = (url) => {
    const link = document.createElement("a")
    link.target = '_blank';
    link.href = url
    link.click()
}

function CheckUpTable({data, fileName, pymolUrl, dynamicColumns}) {
    const {Column, ColumnGroup} = Table;
    const [modelOpen, setModalOpen] = useState(false);
    const [filteredData, setFilteredData] = useState(data)
    const [isPending, setPending] = useState(false)
    const rowMergedData = useRef(defineMerge(data));
    const { outlierLowerBound, allowedLowerBound } = useContext(ConfigContext);
    const breakpoints = Grid.useBreakpoint();

    const [toggleView, setToggleView] = useState(null)
    const [dynamicTableColumns, setDynamicTableColumns] = useState([])

    const singleDataHolder = useCallback((entry) => {
        const compression = entry ? entry["compression"] : ''
        const baseNode = <div style={{
            background: getCompressionColor(compression ? parseFloat(compression) : null, outlierLowerBound, allowedLowerBound),
            padding: '14%'
        }}>
            <span >{compression}</span><span style={{color:"#808080", fontSize:"0.8em"}}>{compression ? ' bits' : ''}</span>
        </div>

        return getCompressionAdditionalInfo(baseNode, entry, outlierLowerBound, allowedLowerBound)
    }, [allowedLowerBound, outlierLowerBound])

    const DualDataHolder = useCallback((entry) => {
        const compression = entry ? entry["compression"] : ''
        const baseNode =
            <div
                style={{background: getCompressionColor(compression ? parseFloat(compression) : null, outlierLowerBound, allowedLowerBound)}}>
                <span >{compression}</span><span style={{color:"#808080", fontSize:"0.8em"}}>{compression ? ' bits' : ''}</span>
                <Divider style={{padding: 0, margin: 5}}/>
                <span style={{
                    fontSize: "0.85em",
                    color: "#595959"
                }}> {(entry && entry['angle']) ? `(${entry['angle']}º)` : ''}</span>
            </div>

        return getCompressionAdditionalInfo(baseNode, entry, outlierLowerBound, allowedLowerBound)
    }, [allowedLowerBound, outlierLowerBound])

    const SingleOrDualHolder = useCallback((entry, isDihedralAngle) => {
        return isDihedralAngle ? DualDataHolder(entry) : singleDataHolder(entry)
    }, [DualDataHolder,singleDataHolder])

    const ModelInfo = (model) => {
        const {modelText, toolTipText} = getModel(model)
        const tagColor = modelText === "a.a specific" ? "cyan" : "volcano"
        return (<Tooltip title={`${toolTipText}`} color="blue">
            <Tag size={"small"} color={tagColor} style={{whiteSpace: "normal"}}>{modelText}</Tag>
        </Tooltip>)
    }

    useEffect(() => {
        setFilteredData(data)
    }, [data])

    useEffect(() => {

        const columns = []

        if (dynamicColumns.joint) {
            columns.push(<Column width={100} key={`joint`} title="joint" dataIndex="joint" align={"center"}
                                 render={singleDataHolder} {...generateThresholdFilterProps('joint')}/>)
        }

        if (dynamicColumns.marginal.length > 0) {
            const marginalColumns = dynamicColumns.marginal.map((conformation, index) => (
                <Column width={100} key={`marginal-${index}`} title={conformation.name} dataIndex={conformation.key}
                        render={toggleView && (isIndividual(conformation.key)) ? (data) => RotatableCircle(data, outlierLowerBound, allowedLowerBound) : (data) => SingleOrDualHolder(data, isIndividual(conformation.key))}
                        align={"center"} {...generateThresholdFilterProps(conformation.key)}/>
            ))
            columns.push(<ColumnGroup key="marginal-group" title="Marginal"> {marginalColumns} </ColumnGroup>)
        }

        if (dynamicColumns.conditioned.length > 0) {
            const conditionedColumns = dynamicColumns.conditioned.map((conformation, index) => (
                <Column width={100} key={`conditioned-${index}`}
                        title={`${conformation.name.up} | ${conformation.name.down}`}
                        dataIndex={`${conformation.key.up}|${conformation.key.down}`} align={"center"}
                        render={singleDataHolder} {...generateThresholdFilterProps(`${conformation.key.up}|${conformation.key.down}`)}/>
            ))
            columns.push(<ColumnGroup key="conditioned-group" title="Conditioned"> {conditionedColumns} </ColumnGroup>)
        }
        setDynamicTableColumns(columns)

    }, [dynamicColumns, outlierLowerBound, allowedLowerBound, toggleView, singleDataHolder, DualDataHolder, SingleOrDualHolder])


    const generateThresholdFilterProps = (dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={null}
                    placeholder={`Filter ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => {
                        confirm()
                    }}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            confirm()
                        }}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters()
                            setSelectedKeys([])
                            confirm()
                        }}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) => {
            return record && record[dataIndex] && record[dataIndex]['compression'] && parseFloat(record[dataIndex]['compression']) < value
        }
    });
    return (

        <Card style={{overflowX:"auto", paddingLeft:10, paddingRight:10,scrollbarWidth:'thin'}}>
            <div style={{minWidth: "1400px", width: 'min-content'}}>
                <Spin indicator={antIcon} spinning={isPending} style={{top: '40%'}}>
                    <Row style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between'}}>
                        <Col align="left" xl={9} lg={10}
                             style={{display: 'flex', justifyContent: 'space-between', columnGap: "0.3rem"}}>
                            <div>
                                <Tag style={{fontSize: "1.0em", color: 'black', fontWeight: "inherit"}}
                                     bordered={false} color={'white'}>Visualize dihedral <AngleSvg/></Tag>
                                <Switch onChange={value => startTransition(() => setPending((_) => {
                                    setTimeout(() => {
                                        setToggleView(value);
                                        setPending(false)
                                    }, 0)
                                    return true
                                }))} disabled={filteredData.length === 0}/>
                            </div>
                            <Button type="primary" disabled={filteredData.length === 0} onClick={() => {
                                generateFile(filteredData, dynamicColumns, fileName)
                            }}>Download Report</Button>
                            <Button type="primary" disabled={filteredData.length === 0} onClick={() => {
                                downloadPymol(pymolUrl)
                            }}>Download annotated PyMOL</Button>
                        </Col>
                        <Col>
                            <Tooltip title="Dihedral angle group nomenclature" color={"blue"}>
                                <Button type="primary" icon={<InfoCircleOutlined/>} onClick={()=>{
                                    setModalOpen(true)
                                }}>Nomenclature</Button>
                            </Tooltip>
                            <Modal
                                width={breakpointsHandler({
                                    xs: '100%',
                                    sm: "90%",
                                    md: "80%",
                                    lg: "80%",
                                    xl: "75%",
                                    xxl: "50%"
                                }, breakpoints)}
                                open={modelOpen}
                                closable={false}
                                onOk={() => {setModalOpen(false)}}
                                cancelButtonProps={{ style: { display: 'none' } }}
                            >
                                <Alert
                                    style={{marginBottom:"min(3vh, 22px)"}}
                                    message={modelOpen ? <b>Dihedral angle group nomenclature</b> : "Dihedral angle group nomenclature"}
                                    type="info"
                                    showIcon
                                    description={modelOpen ?
                                        <List
                                            size="small"
                                            dataSource={DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS}
                                            renderItem={(item, index) => (
                                                <List.Item style={{paddingBottom: 4, paddingTop: (index === 0 ? 6 : 4)}}>
                                                    <Row style={{width: '100%'}}>
                                                        <Col md={6} lg={5} xl={4} xxl={4}>
                                                            <Typography.Text strong style={{fontSize: 15}}>{item[0]}</Typography.Text>
                                                        </Col>
                                                        <Col md={19} lg={19} xl={20} xxl={20} style={{fontSize: '0.9rem'}}>
                                                            {item[1]}
                                                        </Col>
                                                    </Row>
                                                </List.Item>
                                            )}
                                        /> : ""}/>
                            </Modal>
                        </Col>
                    </Row>
                    <div
                        style={{
                            height: "calc(100vh - 150px)",
                            overflow: "auto"
                        }}
                    >

                        <Table dataSource={defineMerge(data)}
                               sticky
                               bordered
                               loading={false}
                               size="small"
                               pagination={false}
                               onChange={(pagination, filters, sorter, extra) => {
                                   rowMergedData.current = defineMerge(extra.currentDataSource)
                                   setFilteredData(extra.currentDataSource)
                               }}
                        >
                            <Column onCell={(record, index) => {
                                return {
                                    children: record["chain"],
                                    rowSpan: rowMergedData.current[index]["mergeColumns"]
                                }
                            }
                            } align={"center"} width={100} title="chain" dataIndex="chain"/>
                            <Column onCell={(record, index) => {
                                return {
                                    children: record["amino_acid"],
                                    rowSpan: rowMergedData.current[index]["mergeColumns"]
                                }
                            }
                            } align={"center"} width={100} title="amino acid" dataIndex="amino_acid"/>
                            <Column onCell={(record, index) => {
                                return {
                                    children: record["index"],
                                    rowSpan: rowMergedData.current[index]["mergeColumns"]
                                }
                            }
                            } align={"center"} width={100} title="index" dataIndex="index"/>
                            <Column align={"center"} width={100} title="model" dataIndex="model" render={ModelInfo}/>
                            {dynamicTableColumns}
                        </Table>
                    </div>
                </Spin>
            </div>
        </Card>


    )
}

export default CheckUpTable;